import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress } from '@mui/material';
import { TrendingUp, Assessment, AttachMoney, Sync } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [topLicitaciones, setTopLicitaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {}),
      api.get('/licitaciones/top?limit=10').then(r => {
        const data = r.data;
        setTopLicitaciones(Array.isArray(data) ? data : (data?.items || []));
      }).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const categories = stats?.categorias || stats?.categories || [];

  const statCards = [
    { title: 'Total Licitaciones', value: stats?.total_licitaciones || stats?.total || 0, icon: <Assessment />, color: '#0047AB' },
    { title: 'IT Relevantes', value: stats?.total_it || stats?.it_count || 0, icon: <TrendingUp />, color: '#00A3FF' },
    { title: 'Monto Total', value: `$${((stats?.total_monto || 0) / 1000000).toFixed(0)}M`, icon: <AttachMoney />, color: '#0070d4' },
    { title: 'Ultimo Sync', value: stats?.ultima_sync ? new Date(stats.ultima_sync).toLocaleDateString('es-CL') : 'N/A', icon: <Sync />, color: '#003087' },
  ];

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statCards.map((card, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ background: `linear-gradient(135deg, ${card.color} 0%, ${card.color}dd 100%)`, color: 'white' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ fontSize: 40, opacity: 0.8 }}>{card.icon}</Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{card.value}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>{card.title}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Licitaciones por Categoria</Typography>
              {categories.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={categories}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="categoria" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="url(#blueGradient)" radius={[4, 4, 0, 0]} />
                    <defs>
                      <linearGradient id="blueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00A3FF" />
                        <stop offset="100%" stopColor="#0047AB" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Sin datos de categorias</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Top Licitaciones</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Codigo</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topLicitaciones.map((l) => (
                      <TableRow key={l.codigo} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/licitaciones/${l.codigo}`)}>
                        <TableCell><Chip label={l.codigo} size="small" color="primary" variant="outlined" /></TableCell>
                        <TableCell sx={{ maxWidth: 200 }} noWrap>{l.nombre}</TableCell>
                        <TableCell align="right">{l.monto_estimado ? `$${Number(l.monto_estimado).toLocaleString('es-CL')}` : '-'}</TableCell>
                      </TableRow>
                    ))}
                    {topLicitaciones.length === 0 && (
                      <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}>Sin datos disponibles</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
